import React, { Component } from 'react';
import { Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { getModels, getActiveModel, activateModel, retrainModel, getFeedbackSummary } from '../services/api';

class Models extends Component {
  constructor(props) {
    super(props);
    this.state = {
      models: [],
      activeModel: null,
      feedbackSummary: null,
      loading: true,
      retraining: false,
      error: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    try {
      const [modelsData, activeModel, feedbackSummary] = await Promise.all([
        getModels(),
        getActiveModel().catch(() => null),
        getFeedbackSummary(),
      ]);
      
      this.setState({ 
        models: modelsData.models || [],
        activeModel,
        feedbackSummary,
        loading: false 
      });
    } catch (error) {
      this.setState({ 
        error: 'Failed to load models',
        loading: false 
      });
    }
  };

  handleActivate = async (modelId) => {
    try {
      await activateModel(modelId);
      this.props.showAlert('Model activated successfully', 'success');
      this.loadData();
    } catch (error) {
      this.props.showAlert('Failed to activate model', 'danger');
    }
  };

  handleRetrain = async () => {
    this.setState({ retraining: true });
    
    try {
      const result = await retrainModel();
      
      if (result.error) {
        this.props.showAlert(result.error, 'warning');
      } else {
        this.props.showAlert(
          `New model ${result.new_model?.version} created! ${result.activated ? 'Activated.' : ''}`,
          'success'
        );
      }
      this.loadData();
    } catch (error) {
      this.props.showAlert('Failed to retrain model', 'danger');
    } finally {
      this.setState({ retraining: false });
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  render() {
    const { models, activeModel, feedbackSummary, loading, retraining, error } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading models...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h4>{error}</h4>
        </div>
      );
    }

    const feedbackStats = feedbackSummary?.feedback_stats || {};
    const canRetrain = (feedbackStats.total_feedback || 0) >= 50;

    return (
      <div>
        <h2 className="mb-4">Model Management</h2>

        {/* Active Model Info */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>Active Model</Card.Header>
              <Card.Body>
                {activeModel ? (
                  <Table size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Version</strong></td>
                        <td>
                          <Badge variant="primary">{activeModel.version}</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Type</strong></td>
                        <td>{activeModel.model_type}</td>
                      </tr>
                      <tr>
                        <td><strong>Contamination Rate</strong></td>
                        <td>{(activeModel.contamination_rate * 100).toFixed(1)}%</td>
                      </tr>
                      <tr>
                        <td><strong>Training Samples</strong></td>
                        <td>{activeModel.training_samples || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Feedback Incorporated</strong></td>
                        <td>{activeModel.feedback_incorporated || 0}</td>
                      </tr>
                      <tr>
                        <td><strong>Created</strong></td>
                        <td>{this.formatDate(activeModel.created_at)}</td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted">
                    <p>No active model</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card>
              <Card.Header>Model Performance</Card.Header>
              <Card.Body>
                {activeModel?.metrics ? (
                  <Table size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Precision</strong></td>
                        <td>
                          {activeModel.metrics.precision 
                            ? `${(activeModel.metrics.precision * 100).toFixed(1)}%`
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Recall</strong></td>
                        <td>
                          {activeModel.metrics.recall 
                            ? `${(activeModel.metrics.recall * 100).toFixed(1)}%`
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>F1 Score</strong></td>
                        <td>
                          {activeModel.metrics.f1_score 
                            ? `${(activeModel.metrics.f1_score * 100).toFixed(1)}%`
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted">
                    <p>No performance metrics available</p>
                    <small>Submit feedback to calculate metrics</small>
                  </div>
                )}

                <hr />

                <h6>Current Precision (from feedback)</h6>
                <div className="d-flex align-items-center">
                  <div className="progress flex-grow-1 mr-2">
                    <div 
                      className="progress-bar bg-success" 
                      style={{ width: `${(feedbackStats.precision || 0) * 100}%` }}
                    />
                  </div>
                  <strong>{((feedbackStats.precision || 0) * 100).toFixed(1)}%</strong>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Retraining */}
        <Card className="mb-4">
          <Card.Header>Model Retraining</Card.Header>
          <Card.Body>
            <Row>
              <Col md={8}>
                <p>
                  Retrain the anomaly detection model using collected feedback.
                  The new model will adjust its contamination rate based on the 
                  ratio of true to false positives.
                </p>
                
                <Alert variant={canRetrain ? 'success' : 'info'}>
                  <strong>Feedback Progress:</strong> {feedbackStats.total_feedback || 0} / 50 required
                  {canRetrain && feedbackSummary?.retrain_recommended && (
                    <span className="ml-2">
                      <Badge variant="warning">Retraining Recommended</Badge>
                    </span>
                  )}
                </Alert>
              </Col>
              <Col md={4} className="text-right">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={this.handleRetrain}
                  disabled={!canRetrain || retraining}
                >
                  {retraining ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-2" />
                      Retraining...
                    </>
                  ) : (
                    '🔄 Retrain Model'
                  )}
                </Button>
                {!canRetrain && (
                  <p className="text-muted small mt-2">
                    Need {50 - (feedbackStats.total_feedback || 0)} more feedback records
                  </p>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Model History */}
        <Card>
          <Card.Header>Model History ({models.length})</Card.Header>
          <Card.Body>
            {models.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🤖</div>
                <h4>No models yet</h4>
                <p>Models will appear here after processing data</p>
              </div>
            ) : (
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Version</th>
                    <th>Type</th>
                    <th>Contamination</th>
                    <th>Precision</th>
                    <th>Feedback Used</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={model.id} className={model.is_active ? 'table-success' : ''}>
                      <td>{model.id}</td>
                      <td>
                        <Badge variant={model.is_active ? 'success' : 'secondary'}>
                          {model.version}
                        </Badge>
                      </td>
                      <td>{model.model_type}</td>
                      <td>{(model.contamination_rate * 100).toFixed(1)}%</td>
                      <td>
                        {model.metrics?.precision 
                          ? `${(model.metrics.precision * 100).toFixed(1)}%`
                          : 'N/A'}
                      </td>
                      <td>{model.feedback_incorporated || 0}</td>
                      <td>
                        {model.is_active ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>{this.formatDate(model.created_at)}</td>
                      <td>
                        {!model.is_active && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => this.handleActivate(model.id)}
                          >
                            Activate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Model Info */}
        <Card className="mt-4">
          <Card.Header>About the Models</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h5>Isolation Forest</h5>
                <p>
                  Isolation Forest isolates anomalies by randomly selecting features 
                  and splitting values. Anomalies require fewer splits to isolate, 
                  making them easier to detect.
                </p>
                <ul>
                  <li>Works well with high-dimensional data</li>
                  <li>O(n log n) time complexity</li>
                  <li>100 estimators (trees)</li>
                </ul>
              </Col>
              <Col md={6}>
                <h5>Local Outlier Factor (LOF)</h5>
                <p>
                  LOF measures the local density deviation of a point compared to 
                  its neighbors. Points with lower density than their neighbors 
                  are considered outliers.
                </p>
                <ul>
                  <li>Effective for varying cluster densities</li>
                  <li>Uses k-nearest neighbors (k=20)</li>
                  <li>Novelty detection mode enabled</li>
                </ul>
              </Col>
            </Row>
            <hr />
            <h5>Ensemble Approach</h5>
            <p>
              Our hybrid approach combines both algorithms. A data point is flagged 
              as an anomaly if <strong>either</strong> Isolation Forest or LOF detects it.
              This reduces false negatives while maintaining good precision through 
              the feedback loop.
            </p>
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default Models;